import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import SettingsForm, { FieldDef } from './SettingsForm';
import MaterialSymbol from '../common/MaterialSymbol';
import platformSettingsService from '../../services/platformSettingsService';
import type { PlatformSettings } from '../../types';

export const ISSUER_FIELDS: FieldDef[] = [
    { key: 'name', wide: true },
    { key: 'legal_representative' },
    { key: 'email' },
    { key: 'street' },
    { key: 'zip' },
    { key: 'city' },
    { key: 'country' },
    { key: 'website' },
    { key: 'vat_id' },
    { key: 'tax_number' },
    { key: 'iban' },
    { key: 'bic' },
    { key: 'register_court' },
    { key: 'commercial_register' },
    { key: 'primary_color' },
    { key: 'footer', type: 'multiline', wide: true },
];

export const MAX_LOGO_BYTES = 256 * 1024;

const readAsDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

/** Aussteller der Provisionsrechnungen (PR-…): Stammdaten, Bank, Fußzeile, Logo (Data-URI). */
const IssuerTab: React.FC<{ data: PlatformSettings; onChange: (s: PlatformSettings) => void }> = ({ data, onChange }) => {
    const translate = useTranslate();
    const fileRef = useRef<HTMLInputElement>(null);
    const [logo, setLogo] = useState<string>(data.issuer.logo_url?.source === 'db' ? data.issuer.logo_url.value : '');
    const [logoError, setLogoError] = useState<string | null>(null);
    const envLogo = data.issuer.logo_url?.source === 'env' ? data.issuer.logo_url.value : '';

    const pick = async (file: File | undefined) => {
        setLogoError(null);
        if (!file) return;
        if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type)) {
            setLogoError(translate('platformSettings.issuer.logoType'));
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            setLogoError(translate('platformSettings.issuer.logoSize'));
            return;
        }
        setLogo(await readAsDataUri(file));
    };

    const shown = logo || envLogo;

    return (
        <SettingsForm
            group="issuer"
            labelPrefix="platformSettings.issuer.fields"
            defs={ISSUER_FIELDS}
            data={data.issuer}
            onSave={async (values) => onChange(await platformSettingsService.update({ issuer: { ...values, logo_url: logo } }))}
        >
            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{translate('platformSettings.issuer.logo')}</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ width: 160, height: 64, border: '1px solid var(--oa-divider)', borderRadius: 'var(--oa-radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--oa-surface)' }}>
                        {shown ? <img src={shown} alt={translate('platformSettings.issuer.logo')} data-testid="issuer-logo-preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                            : <MaterialSymbol name="receipt_long" size={28} />}
                    </Box>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden data-testid="issuer-logo-input" onChange={(e) => pick(e.target.files?.[0])} />
                    <Button variant="outlined" startIcon={<MaterialSymbol name="upload" size={18} />} onClick={() => fileRef.current?.click()}>
                        {translate('platformSettings.issuer.logoUpload')}
                    </Button>
                    {logo && (
                        <Button variant="text" onClick={() => setLogo('')} data-testid="issuer-logo-remove">{translate('platformSettings.issuer.logoRemove')}</Button>
                    )}
                </Box>
                <Typography variant="body2" sx={{ mt: 1, color: 'var(--oa-text-2)' }}>{translate('platformSettings.issuer.logoHint')}</Typography>
                {logoError && <Alert severity="error" sx={{ mt: 1 }}>{logoError}</Alert>}
            </Box>
            <Alert severity="info" sx={{ mt: 3 }}>{translate('platformSettings.issuer.requirement')}</Alert>
        </SettingsForm>
    );
};

export default IssuerTab;
